// v0.3.0: Tiled-JSON map loader shared by the OfficeDO and the browser client.
// v1.0.0: Gather-style TOP-DOWN world — 4-direction movement with wall/furniture collision,
//         seats with a facing, rooms as private zones, and user placements layered on top.
// The movement rule (tryStep) lives here so the DO and the client predict identically.

export interface TiledProperty { name: string; type: string; value: unknown }
export interface TiledObject {
  id: number; name: string; type: string; x: number; y: number; width: number; height: number;
  properties?: TiledProperty[];
}
export interface TiledTileLayer { type: 'tilelayer'; name: string; width: number; height: number; data: number[] }
export interface TiledObjectLayer { type: 'objectgroup'; name: string; objects: TiledObject[] }
export interface TiledTileset { firstgid: number; name: string; tiles?: { id: number; type?: string }[] }
export interface TiledMap {
  width: number; height: number; tilewidth: number; tileheight: number;
  layers: (TiledTileLayer | TiledObjectLayer)[];
  tilesets: TiledTileset[];
}

export interface Zone { name: string; x: number; y: number; w: number; h: number; meetUrl: string | null }
export type Dir4 = 'up' | 'down' | 'left' | 'right';
/** v1.0.0: seats and chairs face one of four ways */
export type Facing = Dir4;
export const FACINGS: Facing[] = ['down', 'up', 'left', 'right'];

/** v0.18.0: something a user has put in the office. */
export type PlaceKind = 'table' | 'chair' | 'block' | 'platform' | 'decor';
export type PlaceLayer = 'terrain' | 'object' | 'decor';
export interface Placement { id: number; kind: PlaceKind; style: number; x: number; y: number; face: Facing }
export function placementLayer(kind: PlaceKind): PlaceLayer {
  return kind === 'block' || kind === 'platform' ? 'terrain' : kind === 'decor' ? 'decor' : 'object';
}
/** base floor you can't build on */
const STRUCTURAL = new Set(['wall', 'window', 'glass', 'door']);
/** seat object types → facing */
const SEAT_TYPES: Record<string, Facing> = { chair_d: 'down', chair_u: 'up', chair_l: 'left', chair_r: 'right', sofa: 'down', sofa_u: 'up', bench: 'down' };

export interface OfficeMap {
  width: number;
  height: number;
  tile: number;
  floor: number[];
  objects: number[];
  collision: Uint8Array;
  zones: Zone[];
  spawn: { x: number; y: number };
  /** gid -> tile type name */
  tileTypes: Map<number, string>;
  /** tiles that open a Meet link, keyed "x,y" -> zone */
  meetTiles: Map<string, Zone>;
  /** sit-able tiles, keyed "x,y" -> 'chair' | 'sofa' | 'bench' (base + placements) */
  seats: Map<string, string>;
  /** which way a seat turns you */
  seatFace: Map<string, Facing>;
  /** personal desks, keyed "x,y" (the chair) -> desk id */
  desks: Map<string, string>;
  /** desk id -> chair tile */
  deskPos: Map<string, { x: number; y: number }>;
  // ---- v0.18.0: placements ----
  placements: Map<number, Placement>;
  /** derived from placements — rebuilt by rebuildDynamic() */
  dynSolid: Uint8Array;
  dynObjects: Map<string, Placement>;
  rugs: Map<string, Placement>;
  decor: Map<string, Placement>;
  baseSeats: Map<string, string>;
  baseSeatFace: Map<string, Facing>;
}

export function loadMap(raw: TiledMap): OfficeMap {
  const tile = raw.tilewidth;
  const layer = (name: string) => raw.layers.find((l) => l.type === 'tilelayer' && l.name === name) as TiledTileLayer | undefined;
  const group = (name: string) => raw.layers.find((l) => l.type === 'objectgroup' && l.name === name) as TiledObjectLayer | undefined;

  const floor = layer('floor')?.data ?? [];
  const objects = layer('objects')?.data ?? [];
  const col = layer('collision')?.data ?? [];
  const n = raw.width * raw.height;
  const collision = new Uint8Array(n);
  for (let i = 0; i < n; i++) collision[i] = col[i] ? 1 : 0;

  const tileTypes = new Map<number, string>();
  for (const ts of raw.tilesets) for (const t of ts.tiles ?? []) if (t.type) tileTypes.set(ts.firstgid + t.id, t.type);

  const zones: Zone[] = (group('zones')?.objects ?? []).map((o) => ({
    name: o.name,
    x: Math.round(o.x / tile), y: Math.round(o.y / tile),
    w: Math.round(o.width / tile), h: Math.round(o.height / tile),
    meetUrl: (o.properties?.find((p) => p.name === 'meetUrl')?.value as string | undefined) ?? null,
  }));

  const spawnObj = group('meta')?.objects.find((o) => o.type === 'spawn');
  const spawn = spawnObj ? { x: Math.round(spawnObj.x / tile), y: Math.round(spawnObj.y / tile) } : { x: 1, y: 1 };

  const meetTiles = new Map<string, Zone>();
  const baseSeats = new Map<string, string>();
  const baseSeatFace = new Map<string, Facing>();
  for (let y = 0; y < raw.height; y++) for (let x = 0; x < raw.width; x++) {
    const type = tileTypes.get(objects[y * raw.width + x]);
    if (!type) continue;
    if (type === 'meet') {
      const z = zones.find((zz) => inRect(x, y, zz));
      if (z) meetTiles.set(`${x},${y}`, z);
    } else if (type in SEAT_TYPES) {
      baseSeats.set(`${x},${y}`, type.startsWith('chair') ? 'chair' : type.startsWith('sofa') ? 'sofa' : 'bench');
      baseSeatFace.set(`${x},${y}`, SEAT_TYPES[type]);
    }
  }

  const desks = new Map<string, string>();
  const deskPos = new Map<string, { x: number; y: number }>();
  for (const o of group('desks')?.objects ?? []) {
    const x = Math.round(o.x / tile), y = Math.round(o.y / tile);
    desks.set(`${x},${y}`, o.name);
    deskPos.set(o.name, { x, y });
  }

  return {
    width: raw.width, height: raw.height, tile, floor, objects, collision, zones, spawn, tileTypes, meetTiles,
    seats: new Map(baseSeats), seatFace: new Map(baseSeatFace), desks, deskPos,
    placements: new Map(), dynSolid: new Uint8Array(n), dynObjects: new Map(), rugs: new Map(), decor: new Map(),
    baseSeats, baseSeatFace,
  };
}

function inRect(x: number, y: number, z: Zone): boolean {
  return x >= z.x && x < z.x + z.w && y >= z.y && y < z.y + z.h;
}

// ---------- v0.18.0: placements ----------

/** Can this kind go on this tile? (decor goes anywhere in bounds) */
export function canPlace(map: OfficeMap, kind: PlaceKind, x: number, y: number): boolean {
  if (!inBounds(map, x, y)) return false;
  if (kind === 'decor') return true;
  const ft = map.tileTypes.get(map.floor[y * map.width + x]);
  if (ft && STRUCTURAL.has(ft)) return false;
  if (map.collision[y * map.width + x]) return false;
  return true;
}

/** Replace all placements and rebuild the derived tables. */
export function setPlacements(map: OfficeMap, list: Placement[]): void {
  map.placements = new Map(list.map((p) => [p.id, p]));
  rebuildDynamic(map);
}

/** Add one placement (evicting whatever shared its layer + tile). Returns the evicted ids. */
export function addPlacement(map: OfficeMap, p: Placement): number[] {
  const layer = placementLayer(p.kind);
  const evicted: number[] = [];
  for (const q of map.placements.values()) if (q.x === p.x && q.y === p.y && placementLayer(q.kind) === layer) evicted.push(q.id);
  for (const id of evicted) map.placements.delete(id);
  map.placements.set(p.id, p);
  rebuildDynamic(map);
  return evicted;
}

export function removePlacement(map: OfficeMap, id: number): boolean {
  const ok = map.placements.delete(id);
  if (ok) rebuildDynamic(map);
  return ok;
}

/** The placements on a tile, topmost first (decor over objects over terrain). */
export function placementsAt(map: OfficeMap, x: number, y: number): Placement[] {
  const order: PlaceLayer[] = ['decor', 'object', 'terrain'];
  return [...map.placements.values()].filter((p) => p.x === x && p.y === y).sort((a, b) => order.indexOf(placementLayer(a.kind)) - order.indexOf(placementLayer(b.kind)));
}

function rebuildDynamic(map: OfficeMap): void {
  map.dynSolid.fill(0);
  map.dynObjects.clear(); map.rugs.clear(); map.decor.clear();
  map.seats = new Map(map.baseSeats);
  map.seatFace = new Map(map.baseSeatFace);
  for (const p of map.placements.values()) {
    const i = p.y * map.width + p.x, key = `${p.x},${p.y}`;
    switch (p.kind) {
      case 'block': map.dynSolid[i] = 1; map.dynObjects.set(key, p); break;
      case 'table': map.dynSolid[i] = 1; map.dynObjects.set(key, p); break;
      case 'chair': map.dynObjects.set(key, p); map.seats.set(key, 'chair'); map.seatFace.set(key, p.face); break;
      case 'platform': map.rugs.set(key, p); break;
      case 'decor': map.decor.set(key, p); break;
    }
  }
}

// ---------- queries ----------

export function inBounds(map: OfficeMap, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < map.width && y < map.height;
}

export function isSolid(map: OfficeMap, x: number, y: number): boolean {
  if (!inBounds(map, x, y)) return true;
  const i = y * map.width + x;
  return map.collision[i] === 1 || map.dynSolid[i] === 1;
}

/** A tile you can occupy: in bounds and not solid. */
export function isWalkable(map: OfficeMap, x: number, y: number): boolean {
  return inBounds(map, x, y) && !isSolid(map, x, y);
}

/** A tile a player may rest on (used to validate restored positions). */
export function isStandable(map: OfficeMap, x: number, y: number): boolean {
  return isWalkable(map, x, y);
}

export function dirDelta(dir: Dir4): [number, number] {
  return dir === 'up' ? [0, -1] : dir === 'down' ? [0, 1] : dir === 'left' ? [-1, 0] : [1, 0];
}

/**
 * Top-down step rule: one tile in the pressed direction if it isn't solid.
 * (`_jump` is kept in the signature for the wire format; it does nothing now.)
 */
export function tryStep(map: OfficeMap, x: number, y: number, dir: Dir4, _jump = false): { x: number; y: number } | null {
  const [dx, dy] = dirDelta(dir);
  const nx = x + dx, ny = y + dy;
  return isWalkable(map, nx, ny) ? { x: nx, y: ny } : null;
}

/**
 * v0.16.0: shortest key-press route from `from` to (or as near as possible to) `to`,
 * over the same step rule players use, so the client can replay it as key presses.
 */
export interface PathStep { dir: Dir4; jump: boolean }
export function findPath(map: OfficeMap, from: { x: number; y: number }, to: { x: number; y: number }): { steps: PathStep[]; end: { x: number; y: number } } | null {
  const key = (x: number, y: number) => y * map.width + x;
  const prev = new Map<number, { k: number; dir: Dir4 }>();
  const seen = new Set<number>([key(from.x, from.y)]);
  const queue: { x: number; y: number }[] = [from];
  let best = from, bestD = Math.abs(from.x - to.x) + Math.abs(from.y - to.y);
  const DIRS: Dir4[] = ['left', 'right', 'up', 'down'];
  for (let qi = 0; qi < queue.length && qi < 8000; qi++) {
    const cur = queue[qi];
    const d = Math.abs(cur.x - to.x) + Math.abs(cur.y - to.y);
    if (d < bestD) { best = cur; bestD = d; }
    if (cur.x === to.x && cur.y === to.y) break;
    for (const dir of DIRS) {
      const n = tryStep(map, cur.x, cur.y, dir);
      if (!n) continue;
      const k = key(n.x, n.y);
      if (seen.has(k)) continue;
      seen.add(k);
      prev.set(k, { k: key(cur.x, cur.y), dir });
      queue.push(n);
    }
  }
  if (best.x === from.x && best.y === from.y) return null;
  const steps: PathStep[] = [];
  let k = key(best.x, best.y);
  while (k !== key(from.x, from.y)) { const p = prev.get(k); if (!p) break; steps.push({ dir: p.dir, jump: false }); k = p.k; }
  steps.reverse();
  return { steps, end: best };
}

export function zoneAt(map: OfficeMap, x: number, y: number): Zone | null {
  for (const z of map.zones) if (inRect(x, y, z)) return z;
  return null;
}
